'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var Icon = require('../../Icon/src/Icon.js');
var Spinner = require('../../Spinner/src/Spinner.js');
var TextInput_module = require('../styles/TextInput.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

function fixControlledValue(value) {
    if (typeof value === 'undefined' || value === null) {
        return '';
    }
    return value;
}
/**
 * @description The Text Input component allows user to type in text
 *
 * @component
 * @example
 * ```jsx
 * <TextInput name="field" value="value" />
 * ```
 */
const TextInput = React__namespace.forwardRef((props, ref) => {
    const { id, autoFocus, value, label, placeholder, postfix, icon, leftIcon, disabled, invalid, readOnly, loading, positionInGroup, directionGroup, background, className, type, onFocus, onBlur, onChange, onClick, ...restProps } = props;
    const cx = reactUtils.useStyles(TextInput_module.default);
    const formControlContext = reactUtils.useFormControlContext();
    const containerRef = React__namespace.useRef(null);
    const inputSegmentRef = React__namespace.useRef(null);
    const inputRef = React__namespace.useRef();
    const [focused, setFocused] = reactUtils.useBoolean(false);
    const [filled, setFilled] = reactUtils.useBoolean(value !== undefined && value !== '');
    const inputId = reactUtils.useId(id ? '' : 'textInput', id);
    const isActive = focused && !readOnly;
    const isDisabled = formControlContext.disabled || disabled;
    const isInvalid = formControlContext.invalid || invalid;
    const hasIcon = icon || loading;
    const onlyPlaceholder = !!placeholder && !label;
    const classNames = cx(className, 'container', {
        focused,
        active: isActive,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        [`background-${background}`]: background,
        [`group-${directionGroup}`]: directionGroup,
        [`group-${positionInGroup}`]: positionInGroup,
    });
    const classNamesLabel = cx('label', {
        active: isActive,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'left-margin': !!leftIcon,
    });
    const classNamesPlaceholder = cx('placeholder', {
        active: isActive,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-label': label && focused,
        hidden: label && !focused,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'left-margin': !!leftIcon,
    });
    const classNamesInput = cx('input', {
        active: isActive,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        hidden: !readOnly && !focused,
    });
    const classNamesInputSegment = cx('input-segment', {
        centered: reactUtils.isUndefined(label) && (filled || focused),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'only-placeholder': onlyPlaceholder,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'left-margin': !!leftIcon,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
    });
    const iconElement = React__namespace.useMemo(() => {
        if (loading) {
            return React__namespace.createElement(Spinner.Spinner, { className: cx('icon') });
        }
        if (icon) {
            return React__namespace.createElement(Icon.Icon, { className: cx('icon'), icon: icon });
        }
        return null;
    }, [cx, icon, loading]);
    const postfixElement = React__namespace.useMemo(() => {
        if (postfix && filled)
            return React__namespace.createElement("div", { className: cx('input-postfix') },
                "\u00A0",
                postfix);
        return null;
    }, [postfix, filled, cx]);
    const labelElement = React__namespace.useMemo(() => {
        if (label) {
            return (React__namespace.createElement("label", { htmlFor: inputId, className: classNamesLabel }, label));
        }
        return null;
    }, [inputId, label, classNamesLabel]);
    const placeholderElement = React__namespace.useMemo(() => {
        if ((label && readOnly) || filled) {
            return null;
        }
        if (placeholder) {
            return (React__namespace.createElement("label", { htmlFor: inputId, className: classNamesPlaceholder }, placeholder));
        }
        return null;
    }, [inputId, label, placeholder, filled, readOnly, classNamesPlaceholder]);
    const handleContainerClick = (event) => {
        if (disabled) {
            return;
        }
        inputRef.current?.focus();
        setFocused.on();
        // TODO: Might be worth redefining the onClick event to React.MouseEvent<HtmlDivElement>
        onClick?.(event);
    };
    const handleContainerBlur = (event) => {
        // needs to prevent extra onBlur callback executing on TextInput wrapper
        const relTarget = event.relatedTarget;
        if (containerRef.current?.contains(relTarget)) {
            event.stopPropagation();
        }
    };
    const handleContainerFocus = (event) => {
        // needs to prevent extra onFocus callback executing on TextInput wrapper
        const relTarget = event.relatedTarget;
        if (relTarget === inputSegmentRef.current || relTarget === containerRef.current) {
            event.stopPropagation();
        }
    };
    const handleFocus = React__namespace.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React__namespace.useCallback((event) => {
        const relTarget = event.relatedTarget;
        if (relTarget === inputSegmentRef.current || relTarget === containerRef.current)
            return;
        setFocused.off();
        onBlur?.(event);
    }, [onBlur, setFocused]);
    const handleChange = React__namespace.useCallback((event) => {
        onChange?.(event);
    }, [onChange]);
    React__namespace.useEffect(() => {
        if (value !== undefined && value !== '') {
            setFilled.on();
        }
        else {
            setFilled.off();
        }
    }, [setFilled, value]);
    return (React__namespace.createElement("div", { "data-qa": "TextInput", ref: containerRef, className: classNames, tabIndex: -1, onClick: handleContainerClick, onBlur: handleContainerBlur, onFocus: handleContainerFocus },
        leftIcon && React__namespace.createElement(Icon.Icon, { className: cx('icon', 'left'), icon: leftIcon }),
        React__namespace.createElement("div", { ref: inputSegmentRef, className: classNamesInputSegment, tabIndex: -1 },
            React__namespace.createElement("div", { className: cx('input-wrapper') },
                postfix && React__namespace.createElement("div", { className: cx('input-value') }, fixControlledValue(value)),
                React__namespace.createElement("input", { autoFocus: autoFocus, spellCheck: "false", autoComplete: "off", id: inputId, ref: reactUtils.useMergedRef(inputRef, ref), type: type, value: fixControlledValue(value), disabled: isDisabled, readOnly: readOnly, className: classNamesInput, onFocus: handleFocus, onBlur: handleBlur, onChange: handleChange, ...restProps }),
                postfixElement)),
        labelElement,
        placeholderElement,
        iconElement));
});
TextInput.defaultProps = {
    type: 'text',
};
// Для этого компонента при указании displayName не отображается автодокументация в сторибуке. Надо разобраться с магией
// TextInput.displayName = 'TextInput';

exports.TextInput = TextInput;
exports.fixControlledValue = fixControlledValue;
//# sourceMappingURL=TextInput.js.map
